import axios from 'axios';

import { dataSource } from '../../global/config/data-source';
import { APP_EVENTS } from '../constants/events';
import {
    API_TIMEOUT_MS,
    AUTH_PATH_PATTERN,
    CSRF_HEADER,
    FORBIDDEN_NOTICE_DEBOUNCE_MS,
    MUTATING_METHODS,
    REQUEST_ID_HEADER,
} from '../constants/network';
import { createDemoAdapter } from '../demo/adapter';
import { createRequestId } from '../utils/id';
import { ApiError } from './error';
import { readCsrfCookie } from './session';

function emitWindowEvent(name, detail) {
    if (typeof window === 'undefined') return;
    window.dispatchEvent(new CustomEvent(name, { detail }));
}

function inferContentType(cfg) {
    if (cfg.data == null) return;
    if (cfg.headers['Content-Type']) return;
    if (cfg.data instanceof FormData) {
        delete cfg.headers['Content-Type'];
        return;
    }
    if (cfg.data instanceof Blob) {
        cfg.headers['Content-Type'] = cfg.data.type || 'application/octet-stream';
        return;
    }
    if (cfg.data instanceof URLSearchParams) {
        cfg.headers['Content-Type'] = 'application/x-www-form-urlencoded';
        return;
    }
    if (typeof cfg.data === 'object') cfg.headers['Content-Type'] = 'application/json';
}

export function createApiClient({
    baseURL,
    adapter,
    withCredentials = true,
    emit = emitWindowEvent,
    readCsrf = readCsrfCookie,
} = {}) {
    const client = axios.create({ baseURL, adapter, withCredentials, timeout: API_TIMEOUT_MS });
    let lastForbiddenNotice = 0;

    client.interceptors.request.use((cfg) => {
        inferContentType(cfg);
        if (MUTATING_METHODS.has(String(cfg.method).toLowerCase())) {
            const csrf = readCsrf();
            if (csrf) cfg.headers[CSRF_HEADER] = csrf;
        }
        cfg.metadata = { id: createRequestId(), start: performance.now() };
        cfg.headers[REQUEST_ID_HEADER] = cfg.metadata.id;
        return cfg;
    });

    client.interceptors.response.use(
        (res) => {
            if (import.meta.env.DEV && res.config.metadata) {
                const ms = (performance.now() - res.config.metadata.start).toFixed(0);
                console.debug(`[api] ${res.config.method?.toUpperCase()} ${res.config.url} → ${res.status} (${ms}ms)`);
            }
            return res;
        },
        (error) => {
            const apiError = ApiError.fromAxios(error);
            const url = String(error?.config?.url || '');
            // /auth/* must not re-trigger the expiry flow: /auth/me returning
            // 401 is precisely how the provider learns the session is gone.
            if (apiError.status === 401 && !AUTH_PATH_PATTERN.test(url)) {
                emit(APP_EVENTS.SESSION_EXPIRED);
            }
            if (apiError.status === 403) {
                emit(APP_EVENTS.PERMISSIONS_REFRESH);
                const now = Date.now();
                if (now - lastForbiddenNotice > FORBIDDEN_NOTICE_DEBOUNCE_MS) {
                    lastForbiddenNotice = now;
                    emit(APP_EVENTS.NOTICE, { tone: 'warning', message: 'No tienes permiso para esta acción.' });
                }
            }
            throw apiError;
        },
    );

    return client;
}

export const api = dataSource.isDemo
    ? createApiClient({ baseURL: '/', adapter: createDemoAdapter(), withCredentials: false })
    : createApiClient({ baseURL: dataSource.endpoint });
